import { useState, useEffect, useRef, useCallback } from "react";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useSEO } from "@/hooks/use-seo";
import { Check } from "lucide-react";
import { hostMonthlyCents } from "@shared/pricing";
import { StartFreeButton, TourButton } from "@/components/marketing/cta";
import { SAVINGS_CALCULATOR_SEO } from "@/data/marketing-seo";
import { track } from "@/lib/marketing/analytics";
import {
  HOST_BASE_PRICE,
  TIER_STEPS_SHORT,
  TRIAL_DAYS,
  estimateBreakdown,
} from "@/lib/marketing/pricing-copy";

// ── Types ────────────────────────────────────────────────────────────────────

type BillingModel = "seat" | "room" | "property" | "flat";

interface Tool {
  id: string;
  name: string;
  model: BillingModel;
  defaultPrice: number;
  replaces: string;
}

interface ToolGroup {
  label: string;
  tools: Tool[];
}

// ── Tool catalog ─────────────────────────────────────────────────────────────

const TOOL_GROUPS: ToolGroup[] = [
  {
    label: "Project, tasks & team",
    tools: [
      { id: "monday", name: "Monday.com", model: "seat", defaultPrice: 12, replaces: "Action items & team task tracking" },
      { id: "clickup", name: "ClickUp", model: "seat", defaultPrice: 7, replaces: "Ticketing & task management" },
      { id: "asana", name: "Asana", model: "seat", defaultPrice: 11, replaces: "Task management" },
    ],
  },
  {
    label: "Spreadsheets, data & reporting",
    tools: [
      { id: "gws", name: "Google Workspace", model: "seat", defaultPrice: 14, replaces: "Dashboards & financial reporting" },
      { id: "airtable", name: "Airtable", model: "seat", defaultPrice: 20, replaces: "Structured trackers & data" },
    ],
  },
  {
    label: "Accounting & finance",
    tools: [
      { id: "qbo", name: "QuickBooks Online", model: "flat", defaultPrice: 115, replaces: "Financial reporting & owner statements" },
    ],
  },
  {
    label: "Maintenance & vendors",
    tools: [
      { id: "breezeway", name: "Breezeway", model: "property", defaultPrice: 20, replaces: "Maintenance & vendor coordination" },
      { id: "meld", name: "Property Meld", model: "property", defaultPrice: 25, replaces: "Work orders & maintenance" },
      { id: "turno", name: "Turno", model: "property", defaultPrice: 20, replaces: "Cleaning & turn scheduling" },
    ],
  },
  {
    label: "Access control & smart locks",
    tools: [
      { id: "remotelock", name: "RemoteLock", model: "room", defaultPrice: 6, replaces: "Smart locks & access codes" },
    ],
  },
  {
    label: "Messaging & comms",
    tools: [
      { id: "openphone", name: "OpenPhone", model: "seat", defaultPrice: 19, replaces: "Member & vendor messaging" },
      { id: "eztext", name: "Bulk SMS / EZ Texting", model: "flat", defaultPrice: 50, replaces: "Automated messaging" },
    ],
  },
  {
    label: "CRM",
    tools: [
      { id: "hubspot", name: "HubSpot CRM", model: "seat", defaultPrice: 20, replaces: "Owner & partner CRM" },
    ],
  },
  {
    label: "Automation & documents",
    tools: [
      { id: "zapier", name: "Zapier", model: "flat", defaultPrice: 30, replaces: "Built-in automation — no glue" },
      { id: "docusign", name: "DocuSign", model: "flat", defaultPrice: 25, replaces: "Document handling & e-sign" },
      { id: "dext", name: "Dext / receipts", model: "flat", defaultPrice: 20, replaces: "Receipt & invoice capture (AI)" },
    ],
  },
];

const TYPICAL_STACK = new Set(["monday", "gws", "qbo", "breezeway", "remotelock", "openphone", "turno", "dext"]);

const PADPULSE_CANNOT_DO = [
  "Two-way PadSplit sync",
  "Room-by-room co-living management",
  "Owner portal with monthly owner statements",
  "One source of truth (no glue, no silos)",
];

const ALL_TOOL_IDS = TOOL_GROUPS.flatMap((g) => g.tools.map((t) => t.id));

// ── Helpers ──────────────────────────────────────────────────────────────────

function getDefaultQty(tool: Tool, rooms: number, team: number, properties: number): number {
  if (tool.model === "seat") return team;
  if (tool.model === "room") return rooms;
  if (tool.model === "property") return properties;
  return 0;
}

function calcCost(tool: Tool, qty: number, price: number): number {
  if (tool.model === "flat") return price;
  return qty * price;
}

// ── Count-up hook ─────────────────────────────────────────────────────────────

function useCountUp(target: number, duration = 1200) {
  const [value, setValue] = useState(0);
  const frameRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const fromRef = useRef(0);

  useEffect(() => {
    fromRef.current = value;
    startRef.current = null;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);

    const animate = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const progress = Math.min((ts - startRef.current) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(fromRef.current + (target - fromRef.current) * eased));
      if (progress < 1) frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [target, duration]);

  return value;
}

// ── Bar component ─────────────────────────────────────────────────────────────

function ComparisonBar({
  label,
  sublabel,
  monthly,
  maxMonthly,
  colorClass,
  bgClass,
  animate,
}: {
  label: string;
  sublabel?: string;
  monthly: number;
  maxMonthly: number;
  colorClass: string;
  bgClass: string;
  animate: boolean;
}) {
  const pct = maxMonthly > 0 ? Math.round((monthly / maxMonthly) * 100) : 0;
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!animate) { setWidth(0); return; }
    const t = setTimeout(() => setWidth(pct), 80);
    return () => clearTimeout(t);
  }, [pct, animate]);

  return (
    <div className="space-y-1.5" data-testid={`bar-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <span className="text-sm font-semibold text-foreground">{label}</span>
          {sublabel && <span className="ml-1.5 text-xs text-muted-foreground">{sublabel}</span>}
        </div>
        <span className={`text-sm font-bold tabular-nums ${colorClass}`}>
          ${monthly.toLocaleString()}/mo
        </span>
      </div>
      <div className="h-3 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${bgClass}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface ToolState {
  selected: boolean;
  price: number;
  qty: number;
  qtyManual: boolean;
}

export default function SavingsCalculator() {
  useSEO({
    title: SAVINGS_CALCULATOR_SEO.title,
    description: SAVINGS_CALCULATOR_SEO.description,
    path: SAVINGS_CALCULATOR_SEO.path,
  });

  // Global inputs
  const [rooms, setRooms] = useState(24);
  const [roomsPerProp, setRoomsPerProp] = useState(8);
  const [team, setTeam] = useState(4);

  const properties = Math.max(1, Math.ceil(rooms / (roomsPerProp || 1)));

  // Per-tool state
  const buildInitialState = useCallback((): Record<string, ToolState> => {
    const result: Record<string, ToolState> = {};
    for (const group of TOOL_GROUPS) {
      for (const tool of group.tools) {
        result[tool.id] = {
          selected: false,
          price: tool.defaultPrice,
          qty: getDefaultQty(tool, rooms, team, properties),
          qtyManual: false,
        };
      }
    }
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tools, setTools] = useState<Record<string, ToolState>>(buildInitialState);
  const [calculated, setCalculated] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Sync un-edited quantities when globals change
  useEffect(() => {
    setTools((prev) => {
      const next = { ...prev };
      for (const group of TOOL_GROUPS) {
        for (const tool of group.tools) {
          const ts = prev[tool.id];
          if (!ts.qtyManual && tool.model !== "flat") {
            next[tool.id] = { ...ts, qty: getDefaultQty(tool, rooms, team, properties) };
          }
        }
      }
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, team, roomsPerProp, properties]);

  const toggleTool = (id: string) => {
    setTools((prev) => ({ ...prev, [id]: { ...prev[id], selected: !prev[id].selected } }));
  };

  const setToolPrice = (id: string, val: number) => {
    setTools((prev) => ({ ...prev, [id]: { ...prev[id], price: val } }));
  };

  const setToolQty = (id: string, val: number) => {
    setTools((prev) => ({ ...prev, [id]: { ...prev[id], qty: val, qtyManual: true } }));
  };

  const loadTypicalStack = () => {
    setTools((prev) => {
      const next = { ...prev };
      for (const id of ALL_TOOL_IDS) {
        next[id] = { ...next[id], selected: TYPICAL_STACK.has(id) };
      }
      return next;
    });
  };

  const clearAll = () => {
    setTools((prev) => {
      const next = { ...prev };
      for (const id of ALL_TOOL_IDS) {
        next[id] = { ...next[id], selected: false };
      }
      return next;
    });
    setCalculated(false);
  };

  // Calculations
  const selectedTools = TOOL_GROUPS.flatMap((g) =>
    g.tools.filter((t) => tools[t.id]?.selected).map((t) => ({
      tool: t,
      state: tools[t.id],
      monthlyCost: calcCost(t, tools[t.id].qty, tools[t.id].price),
    }))
  );

  const currentMonthly = selectedTools.reduce((s, x) => s + x.monthlyCost, 0);
  const currentAnnual = currentMonthly * 12;
  // PadPulse is priced per property (`@shared/pricing`), using the
  // calculator's own "≈ N properties" estimate.
  const padpulseMonthly = hostMonthlyCents(properties) / 100;
  const padpulseAnnual = padpulseMonthly * 12;
  const padpulseBreakdown = estimateBreakdown(properties, 0);
  const savingsAnnual = currentAnnual - padpulseAnnual;
  const saving = savingsAnnual > 0;

  const handleCalculate = () => {
    setCalculated(true);
    track("savings_calculator_used", { rooms, properties });
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const displaySavings = useCountUp(calculated ? Math.abs(savingsAnnual) : 0);
  const maxBar = Math.max(currentMonthly, padpulseMonthly, 1);

  const selectedCount = selectedTools.length;

  return (
    <div className="min-h-screen bg-white dark:bg-background flex flex-col">
      <PublicHeader />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand">
        <section className="pt-28 md:pt-32 pb-14 md:pb-20">
          <div className="container mx-auto px-6 text-center max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 text-sm text-white/70 mb-5">
              <span className="w-2 h-2 bg-brand-accent rounded-full" />
              The all-in-one PadSplit operating system
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4" data-testid="text-calc-heading">
              Running your PadSplits today takes a dozen tools, a dozen logins, and a dozen bills.
            </h1>
            <p className="text-lg md:text-xl text-brand-accent font-semibold mb-3">
              PadPulse is all of it — one place, for less.
            </p>
            <p className="text-white/70 text-base">
              Enter your portfolio size, select the tools you're currently paying for, and see the real comparison.
            </p>
          </div>
        </section>
      </div>

      <main className="flex-grow" id="main-content">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl py-10 md:py-14 space-y-10">

          {/* Global inputs */}
          <Card data-testid="card-global-inputs">
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold text-foreground mb-5">Your portfolio</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor="input-rooms">
                    Rooms you manage
                  </label>
                  <Input
                    id="input-rooms"
                    type="number"
                    value={rooms}
                    onChange={(e) => setRooms(parseInt(e.target.value) || 0)}
                    onBlur={() => setRooms((v) => Math.max(1, v))}
                    data-testid="input-rooms"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor="input-rooms-per-prop">
                    Rooms per property
                  </label>
                  <Input
                    id="input-rooms-per-prop"
                    type="number"
                    value={roomsPerProp}
                    onChange={(e) => setRoomsPerProp(parseInt(e.target.value) || 0)}
                    onBlur={() => setRoomsPerProp((v) => Math.max(1, v))}
                    data-testid="input-rooms-per-prop"
                  />
                  <p className="text-xs text-muted-foreground" data-testid="text-derived-properties">
                    ≈ {properties} {properties === 1 ? "property" : "properties"}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor="input-team">
                    People on your team
                  </label>
                  <Input
                    id="input-team"
                    type="number"
                    value={team}
                    onChange={(e) => setTeam(parseInt(e.target.value) || 0)}
                    onBlur={() => setTeam((v) => Math.max(1, v))}
                    data-testid="input-team"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tool catalog */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Tools you're currently paying for</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {selectedCount === 0
                    ? "Select tools below or load a typical stack"
                    : `${selectedCount} tool${selectedCount === 1 ? "" : "s"} selected · $${currentMonthly.toLocaleString()}/mo`}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  onClick={loadTypicalStack}
                  className="bg-brand text-brand-foreground hover:bg-brand-hover text-sm"
                  data-testid="button-load-typical-stack"
                >
                  Load a typical stack
                </Button>
                <Button
                  variant="ghost"
                  onClick={clearAll}
                  className="text-sm"
                  data-testid="button-clear-all"
                >
                  Clear all
                </Button>
              </div>
            </div>

            <div className="space-y-6">
              {TOOL_GROUPS.map((group) => (
                <div key={group.label} data-testid={`group-${group.label.toLowerCase().replace(/[\s&,\/]+/g, "-")}`}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">
                    {group.label}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {group.tools.map((tool) => {
                      const ts = tools[tool.id];
                      const isSelected = ts.selected;
                      return (
                        <div
                          key={tool.id}
                          onClick={() => toggleTool(tool.id)}
                          className={`relative rounded-xl border p-4 cursor-pointer transition-all duration-150 select-none ${
                            isSelected
                              ? "border-brand bg-brand-muted ring-1 ring-brand/30"
                              : "border-border bg-card hover:border-brand/40 hover:bg-muted/30"
                          }`}
                          data-testid={`card-tool-${tool.id}`}
                          role="checkbox"
                          aria-checked={isSelected}
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggleTool(tool.id); } }}
                        >
                          {isSelected && (
                            <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-brand flex items-center justify-center">
                              <Check size={11} className="text-white" />
                            </span>
                          )}
                          <p className="font-semibold text-sm text-foreground pr-6">{tool.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{tool.replaces}</p>

                          {isSelected && (
                            <div
                              className="mt-3 pt-3 border-t border-brand/20 grid grid-cols-2 gap-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="space-y-1">
                                <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                                  Price/mo
                                </label>
                                <div className="relative">
                                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                                  <Input
                                    type="number"
                                    min={0}
                                    value={ts.price}
                                    onChange={(e) => setToolPrice(tool.id, Math.max(0, parseFloat(e.target.value) || 0))}
                                    className="pl-5 h-8 text-sm"
                                    data-testid={`input-price-${tool.id}`}
                                  />
                                </div>
                              </div>
                              {tool.model !== "flat" && (
                                <div className="space-y-1">
                                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                                    {tool.model === "seat" ? "Seats" : tool.model === "room" ? "Rooms" : "Properties"}
                                  </label>
                                  <Input
                                    type="number"
                                    min={1}
                                    value={ts.qty}
                                    onChange={(e) => setToolQty(tool.id, Math.max(1, parseInt(e.target.value) || 1))}
                                    className="h-8 text-sm"
                                    data-testid={`input-qty-${tool.id}`}
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          {!isSelected && (
                            <div className="mt-2 flex items-center justify-between">
                              <Badge variant="secondary" className="text-xs font-normal">
                                {tool.model === "flat"
                                  ? `$${tool.defaultPrice}/mo`
                                  : tool.model === "seat"
                                  ? `$${tool.defaultPrice}/seat`
                                  : tool.model === "room"
                                  ? `$${tool.defaultPrice}/room`
                                  : `$${tool.defaultPrice}/property`}
                              </Badge>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Calculate CTA */}
          <div className="flex justify-center pt-2">
            <Button
              size="lg"
              disabled={selectedCount === 0}
              onClick={handleCalculate}
              className="bg-brand-accent hover:bg-brand-accent-hover text-white font-bold px-10 py-6 text-base disabled:opacity-50"
              data-testid="button-calculate"
            >
              Calculate My Savings
            </Button>
          </div>

          {/* Results */}
          {calculated && (
            <div ref={resultsRef} className="space-y-6 pt-4" data-testid="section-results">

              {/* Hero savings figure */}
              <Card className="overflow-hidden">
                <div className={`px-6 py-8 text-center ${saving ? "bg-emerald-50/80 dark:bg-emerald-950/30" : "bg-brand-accent-muted"}`}>
                  {saving ? (
                    <>
                      <p className="text-sm font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-1">
                        Yearly savings switching to PadPulse
                      </p>
                      <p
                        className="text-5xl md:text-6xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums"
                        data-testid="text-savings-figure"
                      >
                        ${displaySavings.toLocaleString()}
                      </p>
                      <p className="text-muted-foreground text-sm mt-2">
                        per year — and you retire {selectedCount} login{selectedCount === 1 ? "" : "s"}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold uppercase tracking-wider text-brand-amber mb-1">
                        PadPulse costs more than your current stack
                      </p>
                      <p
                        className="text-5xl md:text-6xl font-bold text-brand-accent tabular-nums"
                        data-testid="text-savings-figure"
                      >
                        +${displaySavings.toLocaleString()}
                      </p>
                      <p className="text-muted-foreground text-sm mt-2">
                        more per year on PadPulse — but you retire {selectedCount} login{selectedCount === 1 ? "" : "s"} and gain everything below
                      </p>
                    </>
                  )}
                </div>
              </Card>

              {/* Comparison bars */}
              <Card data-testid="card-comparison-bars">
                <CardContent className="pt-6 space-y-5">
                  <h3 className="text-base font-semibold text-foreground">Monthly cost comparison</h3>
                  <ComparisonBar
                    label="Current tools"
                    sublabel={`${selectedCount} tools`}
                    monthly={currentMonthly}
                    maxMonthly={maxBar}
                    colorClass="text-destructive"
                    bgClass="bg-destructive"
                    animate={calculated}
                  />
                  <ComparisonBar
                    label="PadPulse"
                    sublabel={`${properties} ${properties === 1 ? "property" : "properties"}`}
                    monthly={padpulseMonthly}
                    maxMonthly={maxBar}
                    colorClass="text-emerald-600 dark:text-emerald-400"
                    bgClass="bg-emerald-500"
                    animate={calculated}
                  />
                </CardContent>
              </Card>

              {/* Per-tool breakdown */}
              <Card data-testid="card-tool-breakdown">
                <CardContent className="pt-6">
                  <h3 className="text-base font-semibold text-foreground mb-4">Monthly breakdown</h3>
                  <div className="space-y-2">
                    {selectedTools.map(({ tool, state, monthlyCost }) => (
                      <div key={tool.id} className="flex items-center justify-between py-1.5 border-b border-border/60 last:border-0">
                        <div>
                          <span className="text-sm font-medium text-foreground">{tool.name}</span>
                          {tool.model !== "flat" && (
                            <span className="ml-1.5 text-xs text-muted-foreground">
                              ({state.qty} {tool.model === "seat" ? "seats" : tool.model === "room" ? "rooms" : "properties"})
                            </span>
                          )}
                        </div>
                        <span className="text-sm font-semibold tabular-nums text-foreground">
                          ${monthlyCost.toLocaleString()}/mo
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-sm font-bold text-foreground">Total</span>
                      <span className="text-sm font-bold text-destructive tabular-nums" data-testid="text-total-monthly">
                        ${currentMonthly.toLocaleString()}/mo
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* What none of them can do */}
              <Card data-testid="card-exclusive-features">
                <CardContent className="pt-6">
                  <h3 className="text-base font-semibold text-foreground mb-1">What none of them can do</h3>
                  <p className="text-sm text-muted-foreground mb-4">Only PadPulse was built for PadSplit co-living operators.</p>
                  <ul className="space-y-2.5">
                    {PADPULSE_CANNOT_DO.map((item) => (
                      <li key={item} className="flex items-start gap-2.5">
                        <span className="mt-0.5 w-5 h-5 rounded-full bg-brand flex items-center justify-center flex-shrink-0">
                          <Check size={11} className="text-white" />
                        </span>
                        <span className="text-sm text-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Footer band */}
              <div className="rounded-2xl bg-gradient-to-r from-[#0a1628] to-[#0d2447] p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="section-footer-band">
                <div>
                  <p className="text-sm text-white/60 mb-1">PadPulse for 12 months</p>
                  <p className="text-3xl font-bold text-white" data-testid="text-padpulse-annual">
                    ${padpulseAnnual.toLocaleString()}
                    <span className="text-base font-normal text-white/60">/yr</span>
                  </p>
                  <p className="text-xs text-white/50 mt-1" data-testid="text-padpulse-breakdown">
                    {padpulseBreakdown.line} × 12 months
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <StartFreeButton ctaId="savings-start-free" location="savings_calculator" size="default" />
                  <TourButton ctaId="savings-tour" location="savings_calculator" size="default" />
                </div>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <p className="text-xs text-muted-foreground text-center pb-4" data-testid="text-disclaimer">
            Competitor prices are editable estimates based on published plans and vary by plan and portfolio size. PadPulse: {HOST_BASE_PRICE} a property a month, {TIER_STEPS_SHORT}. {TRIAL_DAYS}-day free trial, no card.
          </p>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
